import {
  PostChangePasswordType,
  ProfileType,
  AuthUserType,
  LoginResponseType,
  GetAuthUserForEditResponse,
  UpdateAuthUserRequest,
  DeleteAuthUserResponse,
} from '../types/authUserTypes'
import { livoxApi } from './_livoxApi'

const userApi = livoxApi.injectEndpoints({
  endpoints: (build) => ({
    loginUser: build.mutation<
      LoginResponseType,
      {
        // login with email and password
        username?: string
        password?: string
        token_recaptcha?: string
        // login with Google
        auth_provider?: string
        sso_token?: string
      }
    >({
      query: (body) => {
        return {
          url: '/api/v1/authentication/token/web/auth/',
          method: 'POST',
          body,
          headers: { 'Content-Type': 'application/json' },
        }
      },
    }),
    postResetPassword: build.mutation<void, { email: string }>({
      query: (body) => {
        return {
          url: '/api/v1/authentication/password/reset/',
          method: 'POST',
          body,
          headers: { 'Content-Type': 'application/json' },
        }
      },
    }),
    getUserInfo: build.query<AuthUserType, number>({
      query: () => {
        return {
          url: '/api/v1/authusers/myuser/',
          method: 'GET',
          headers: { 'Content-Type': 'application/json' },
        }
      },
    }),
    getMyProfile: build.query<ProfileType, void>({
      query: () => {
        return {
          url: '/api/v1/authusers/myprofile/',
          method: 'GET',
          headers: { 'Content-Type': 'application/json' },
        }
      },
    }),
    postChangePassword: build.mutation<
      { success: boolean; id_old_password: boolean },
      PostChangePasswordType
    >({
      query: (body) => {
        return {
          url: '/api/v1/authusers/change-password/',
          method: 'PUT',
          body,
          headers: { 'Content-Type': 'application/json' },
        }
      },
    }),
    getValidateResetPasswordToken: build.query<
      { message?: string; error?: string },
      { token: string }
    >({
      query: (params) => {
        return {
          url: `/api/v1/authentication/password/reset/token/?uid=${
            params.token.split('-')[0]
          }&token=${params.token.split('-').slice(1).join('-')}`,
          method: 'GET',
          headers: { 'Content-Type': 'application/json' },
        }
      },
    }),
    postResetPasswordToken: build.mutation<
      { message?: string; error?: string },
      { new_password: string; confirm_password: string; token: string }
    >({
      query: (body) => {
        return {
          url: `/api/v1/authentication/password/reset/token/?uid=${
            body.token.split('-')[0]
          }&token=${body.token.split('-').slice(1).join('-')}`,
          method: 'POST',
          body,
          headers: { 'Content-Type': 'application/json' },
        }
      },
    }),
    uploadPicture: build.mutation<void, File>({
      query: (data) => {
        const formData = new FormData()
        formData.append('file', data)
        return {
          url: '/api/holders/image-upload/',
          method: 'POST',
          body: formData,
        }
      },
    }),
    getAuthUserForEdit: build.query<
      GetAuthUserForEditResponse,
      { uuid: string; role?: string }
    >({
      query: ({ uuid, role }) => {
        const params = role ? `?role=${role}` : ''
        return {
          url: `/api/v1/authusers/${uuid}/${params}`,
          method: 'GET',
          headers: { 'Content-Type': 'application/json' },
        }
      },
    }),
    updateAuthUser: build.mutation<
      { success: string },
      { uuid: string; data: UpdateAuthUserRequest }
    >({
      query: ({ uuid, data }) => {
        return {
          url: `/api/v1/authusers/${uuid}/`,
          method: 'PATCH',
          body: data,
          headers: { 'Content-Type': 'application/json' },
        }
      },
    }),
    deleteAuthUser: build.mutation<DeleteAuthUserResponse, string>({
      query: (uuid) => {
        return {
          url: `/api/v1/authusers/${uuid}/delete/`,
          method: 'DELETE',
          headers: { 'Content-Type': 'application/json' },
        }
      },
    }),
  }),
})

export const {
  useLoginUserMutation,
  usePostResetPasswordMutation,
  useGetUserInfoQuery,
  useGetMyProfileQuery,
  usePostChangePasswordMutation,
  useGetValidateResetPasswordTokenQuery,
  usePostResetPasswordTokenMutation,
  useUploadPictureMutation,
  useLazyGetUserInfoQuery,
  useGetAuthUserForEditQuery,
  useLazyGetAuthUserForEditQuery,
  useUpdateAuthUserMutation,
  useDeleteAuthUserMutation,
} = userApi
